import {
  closeCurrentDialog,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {defaultTo, head, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  GetServiceOrderIssueNoteResponse,
  useBulkDeleteServiceOrderIssueNoteItemsMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
  logError,
} from '@dms/shared';
import {EditMaterialItem} from '@dms/shared-aftersales';
import {OverviewTotalPrice, useWarehouseParams} from '@dms/warehouse/shared-components';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface ListOfItemTabProps extends RequiredTestIdProps {
  priceWithoutVat: string | null;
  priceWithVat: string | null;
  serviceOrderIssueNote?: GetServiceOrderIssueNoteResponse;
}

const PREDEFINED_COLUMNS = ['itemName', 'serviceOrderJobItemId', 'serviceCaseJobId'] as const;

export function ListOfItemTab(props: ListOfItemTabProps) {
  const {serviceOrderIssueNoteId} = useWarehouseParams();
  const [deleteItems] = useBulkDeleteServiceOrderIssueNoteItemsMutation();

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {serviceOrderIssueNoteId}]),
    [serviceOrderIssueNoteId]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = async ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(id)) {
      throw new Error('Row id is not defined');
    }

    await match(actionKey)
      .with('edit', () => {
        const serviceCaseId = defaultTo('', props.serviceOrderIssueNote?.serviceCaseId);
        const serviceOrderId = defaultTo('', props.serviceOrderIssueNote?.serviceOrderId);

        const itemName = getColumnData<string>('itemName')?.value;
        const serviceOrderJobItemId = getColumnData<string>('serviceOrderJobItemId')?.value;
        const serviceCaseJobId = getColumnData<string>('serviceCaseJobId')?.value;

        if (isNil(itemName)) {
          return logError('itemName is not defined');
        }

        if (isNil(serviceCaseJobId)) {
          return logError('serviceCaseJobId is not defined');
        }

        if (isNil(serviceOrderJobItemId)) {
          return logError('serviceOrderJobItemId is not defined');
        }

        const handleClose = () => {
          closeCurrentDialog();
          refreshData();
        };

        openDialog(
          <EditMaterialItem
            itemId={serviceOrderJobItemId}
            serviceCaseId={serviceCaseId}
            serviceOrderId={serviceOrderId}
            serviceJobId={serviceCaseJobId}
            onClose={handleClose}
          />,
          {title: itemName, scrollBehavior: 'outside'}
        );
      })
      .with('delete', () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        openDeleteDialog({
          onConfirm: async () =>
            await deleteItems({
              serviceOrderIssueNoteId,
              body: {serviceOrderIssueNoteItemIds: ids},
            })
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(() => refreshData())
              .catch(handleApiError),
          'data-testid': suffixTestId('deleteItems', props),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <Box height="100%">
      <Box height="95%">
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.issueNoteServiceOrderAddedItems}
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          queryModifier={queryModifier}
          data-testid="issueNoteServiceOrderAddedItemsDatagrid"
        />
      </Box>

      <OverviewTotalPrice
        totalPriceWithVat={props.priceWithVat}
        totalPriceWithoutVat={props.priceWithoutVat}
        data-testid={suffixTestId('totalPrice', props)}
      />
    </Box>
  );
}
